import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { differenceInHours, isAfter } from 'date-fns'
import type { EPaymentMethod, EPaymentType } from '../types/payment'
import { EBookerLocationType } from '@/data/bookerData'

export interface IReservationPaymentModel {
	type: EPaymentType | null
	method: EPaymentMethod | null
}

interface IReservationStateModel {
	request: IReservationRequest | null
	id: string | null
	insurances: any[]
	insurance: IReservationServiceModel | null
	addons: any[]
	abroad: IReservationServiceModel | null
	upsell: any
	discountCode: string | null
	selectedCarID: number | null
	additionalDrivers: number | null
}

export interface IReservationStoreState {
	reservation: IReservationStateModel
	payment: IReservationPaymentModel
	areCostCalculated: boolean
	discountCode: any | null
	locations: {
		start: ILocationModel | null
		end: ILocationModel | null
	} | null
	dates: {
		start: Date
		end: Date
	} | null
	locationHistory: number[]
	locationDeliveryHistory: number[]
	otherEndLocation: boolean | null
	allLocations: ILocationModel[]
}

export interface IReservationStore {
	readonly state: IReservationStoreState
	setLocationHistoryData: () => void
	setBookerDates: (dates: { start: Date; end: Date }) => void
	setBookerLocations: (locations: { start: ILocationModel | null; end: ILocationModel | null }) => void
	setOtherEndLocation: (value: boolean) => void
	calculateDeliveryPrice: (placeID: string, key: EBookerLocationType) => void
	getDays: ComputedRef<number | null>
	getBookerDates: ComputedRef<{ start: Date; end: Date } | null>
	getBookerLocations: ComputedRef<{ start: ILocationModel | null; end: ILocationModel | null } | null>
	getIsStartDateCorrectForRenteon: ComputedRef<boolean>
	deleteLocalStorage: (token?: string) => void
	addLocationToHistory: (locationID: number) => void
	addLocationDeliveryToHistory: (placeID: string, name: string) => void
	getLocationHistory: ComputedRef<number[]>
	getLocationDeliveryHistory: ComputedRef<number[]>
	updateAllLocations: (locations: ILocationModel[]) => void
	getAllLocations: ComputedRef<ILocationModel[]>
	getOtherEndLocation: ComputedRef<boolean | null>
}

export const useReservationStore = defineStore('reservationStore', (): IReservationStore => {
	const _state = reactive<IReservationStoreState>({
		reservation: {
			request: null,
			id: null,
			insurances: [],
			insurance: null,
			addons: [],
			abroad: null,
			upsell: null,
			discountCode: null,
			selectedCarID: null,
			additionalDrivers: null,
		},
		areCostCalculated: false,
		payment: {
			type: null,
			method: null,
		},
		discountCode: null,
		dates: null,
		locations: null,
		otherEndLocation: null,
		locationHistory: [],
		locationDeliveryHistory: [],
		allLocations: [],
	})
	const localStorageToken = 'cf_reservation'
	const localStorageBookerToken = 'cf_booker'
	const localStorageLocationToken = 'cf_loc_history'
	const localStorageLocationDeliveryToken = 'cf_loc_del_history'

	const getDays = computed(() => {
		if (!_state.dates?.start || !_state.dates?.end) return null
		return getRentalDays(_state.dates.start, _state.dates.end)
	})

	const getLocalStorage = (token = localStorageToken) => {
		if (process.server) return null
		const data = localStorage.getItem(token)
		const parsedData = data ? JSON.parse(data) : null
		if (parsedData?.time && differenceInHours(new Date(), new Date(parsedData.time)) > 24) localStorage.removeItem(token)
		// if(parsedData?.discountCode)
		return parsedData || null
	}

	const updateLocalStorage = (token = localStorageToken, state = _state.reservation) => {
		const time = new Date()
		// const time = '2024-04-02T09:56:20.665Z'
		if (time && token !== localStorageLocationToken && token !== localStorageLocationDeliveryToken)
			localStorage.setItem(token, JSON.stringify({ ...state, time }))
		else localStorage.setItem(token, JSON.stringify(state))
	}

	const deleteLocalStorage = (token = localStorageToken) => {
		localStorage.removeItem(token)
		if (token === localStorageBookerToken) {
			_state.dates = { start: null, end: null }
			_state.locations = { start: null, end: null }
		}
	}

	const calculateDeliveryPrice = async (placeID: string, key: EBookerLocationType) => {
		const bookerStore = useBookerStore()
		try {
			const location = await ApiService.calculateDeliveryPrice(placeID, key)
			if (location.origin?.price) bookerStore.setDeliveryCosts(key, location.origin?.price.amount)
		} catch (error) {
			logger.error(error)
			bookerStore.setDeliveryCosts(key, null)
		}
	}

	const setBookerData = () => {
		const data = getLocalStorage(localStorageBookerToken)
		const { getStartDate, getEndDate } = useBookerService()
		const { businessNow } = useDateTimeService()
		const newStartDate = new Date(getStartDate())

		if (data) {
			_state.dates = data?.dates
			if (
				_state.dates?.start &&
				(isAfter(businessNow(), new Date(data?.dates?.start)) || isAfter(newStartDate, new Date(data?.dates?.start)))
			) {
				_state.dates.start = getStartDate()
			}
			newStartDate.setDate(newStartDate.getDate() + 1)
			if (_state.dates?.end && (isAfter(businessNow(), new Date(data?.dates?.end)) || isAfter(newStartDate, new Date(data?.dates?.end)))) {
				_state.dates.end = getEndDate()
			}
			if (data.locations) {
				Object.keys(data.locations).forEach((key) => {
					const location = data.locations[key]
					if (location && location.placeID) calculateDeliveryPrice(location.placeID, key)
				})
				_state.locations = data.locations
			}

			// _state.locations = data.locations
		}
	}
	const setLocationHistoryData = () => {
		const data = getLocalStorage(localStorageLocationToken)
		const data2 = getLocalStorage(localStorageLocationDeliveryToken)
		if (data) {
			_state.locationHistory = data
		}
		if (data2) {
			_state.locationDeliveryHistory = data2
		}
	}

	setBookerData()
	setLocationHistoryData()

	const setOtherEndLocation = (value: boolean) => {
		_state.otherEndLocation = value
	}

	const setBookerDates = (dates: { start: Date; end: Date }) => {
		_state.dates = dates
		updateLocalStorage(localStorageBookerToken, { dates: _state.dates, locations: _state.locations })
	}

	const setBookerLocations = (locations: { start: ILocationModel | null; end: ILocationModel | null }) => {
		_state.locations = locations
		updateLocalStorage(localStorageBookerToken, { dates: _state.dates, locations: _state.locations })
	}

	const LOCATION_HISTORY_LIMIT = 10

	const addLocationToHistory = (locationID: number) => {
		const token = localStorageLocationToken
		const existing = getLocalStorage(token) ?? []
		const data = [locationID, ...existing.filter((id: number) => id !== locationID)].slice(0, LOCATION_HISTORY_LIMIT)
		updateLocalStorage(token, data as never)
		setLocationHistoryData()
	}

	const addLocationDeliveryToHistory = (placeID: number, name: string) => {
		const token = localStorageLocationDeliveryToken
		const existing = getLocalStorage(token) ?? []
		const data = [{ placeID, name }, ...existing.filter((x: { placeID: number; name: string }) => x.placeID !== placeID)].slice(
			0,
			LOCATION_HISTORY_LIMIT,
		)
		updateLocalStorage(token, data as never)
		setLocationHistoryData()
	}

	const updateAllLocations = (locations: ILocationModel[]) => {
		_state.allLocations = locations
	}

	const getIsStartDateCorrectForRenteon = computed(() => {
		const { getStartDate } = useBookerService()
		const minimalStartDate = getStartDate()

		if (!_state.dates?.start) return false
		if (isAfter(new Date(minimalStartDate), new Date(_state.dates.start))) return false
		return true
	})

	const getAllLocations = computed(() => {
		return _state.allLocations
	})

	const getBookerDates = computed(() => {
		return _state.dates
	})

	const getBookerLocations = computed(() => {
		return _state.locations
	})

	const getLocationHistory = computed(() => {
		return _state.locationHistory
	})

	const getLocationDeliveryHistory = computed(() => {
		return _state.locationDeliveryHistory
	})

	const getOtherEndLocation = computed(() => {
		return _state.otherEndLocation
	})

	return {
		state: _state,
		setOtherEndLocation,
		calculateDeliveryPrice,
		setLocationHistoryData,
		getDays,
		getIsStartDateCorrectForRenteon,
		setBookerDates,
		setBookerLocations,
		getBookerDates,
		getBookerLocations,
		deleteLocalStorage,
		addLocationToHistory,
		addLocationDeliveryToHistory,
		getLocationHistory,
		getLocationDeliveryHistory,
		updateAllLocations,
		getAllLocations,
		getOtherEndLocation,
	}
})
